import classNames from 'classnames';
import { useState } from 'react';

import Checkbox from './components/Checkbox';
import Flex from '../Layout/components/Flex';

import { ChevronBoldIcon } from '../../icons/ui';

import styles from './_index.module.scss';

export interface FilterGroupProps {
  className?: string;
  filter: {
    id: string;
    label: string;
    options: Array<{
      helpText?: string;
      id: string;
      isDisabled?: boolean;
      label: string;
      title?: string;
    }>;
    title?: string;
  };
  isDefaultOpen?: boolean;
  onChange: (newValues: { [key: string]: boolean }) => void;
  value: { [key: string]: boolean };
}

const FilterGroup = ({ className, filter, isDefaultOpen, onChange, value }: FilterGroupProps) => {
  const [isGroupExpanded, setIsGroupExpanded] = useState(isDefaultOpen);

  const areAllValuesSelected =
    filter.options?.length > 0 && filter.options?.every(({ id }) => value?.[id]);
  const isAnyValueSelected = filter.options?.some(({ id }) => value?.[id]);

  const toggleAllOptions = () => {
    onChange(filter.options.reduce((acc, { id }) => ({ ...acc, [id]: !isAnyValueSelected }), {}));
  };
  const toggleOption = (optionId: string) => {
    onChange({ ...value, [optionId]: !value?.[optionId] });
  };

  return (
    <div className={classNames(styles['filter-group'], className)}>
      <Flex gap="small">
        <Checkbox
          id={`filter-group-${filter.id}`}
          isChecked={areAllValuesSelected}
          isIndeterminate={isAnyValueSelected}
          label={filter.label}
          onChange={toggleAllOptions}
          title={filter.title}
          value={filter.id}
        />
        <button
          aria-controls={`filters-${filter.id}`}
          aria-expanded={isGroupExpanded}
          className={styles['filter-group-button']}
          id={`filter_group-${filter.id}_expand`}
          onClick={() => setIsGroupExpanded(!isGroupExpanded)}
        >
          <ChevronBoldIcon />
        </button>
      </Flex>

      <ul
        aria-label={filter.title}
        className={styles['filter-group-list']}
        hidden={!isGroupExpanded}
        id={`filters-${filter.id}`}
        role="region"
      >
        {filter.options?.map((option) => (
          <li key={option.id}>
            <Checkbox
              helpText={option.helpText}
              id={`filter_${filter.id}_${option.id}`}
              isChecked={value?.[option.id]}
              isDisabled={option.isDisabled}
              label={option.label}
              onChange={() => toggleOption(option.id)}
              title={option.title}
              value={option.id}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FilterGroup;
